import { ReactNode, useState } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { ChevronDown, ChevronUp } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import { backgroundColorStyles, colors, radii } from "../../../styles/design-system";

export interface CourseSettingsAccordionProps {
  icon: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
}

const CourseSettingsAccordion = ({
  icon,
  title,
  description,
  children,
  expanded,
  onToggle,
}: CourseSettingsAccordionProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <View style={styles.card}>
      <Pressable
        onPress={onToggle}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={[styles.header, isHovered && { ...backgroundColorStyles.bgLight }]}>
        <View style={styles.left}>
          <View style={styles.icon}>{icon}</View>
          <View style={styles.textContainer}>
            <CustomText style={styles.title} useTranslationText={false} text={title} weight="bold" />
            {description && <CustomText style={styles.descriptionText} useTranslationText={false} text={description} />}
          </View>
        </View>

        <View style={styles.right}>
          {expanded ? (
            <ChevronUp size={20} color={colors.grey[500]} />
          ) : (
            <ChevronDown size={20} color={colors.grey[500]} />
          )}
        </View>
      </Pressable>

      {expanded && children && <View style={styles.contentContainer}>{children}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    marginBottom: 24,
    overflow: "hidden",
  },
  header: {
    paddingHorizontal: 24,
    borderColor: colors.grey[200],
    borderBottomWidth: 1,
    paddingVertical: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  left: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    flex: 1,
  },
  textContainer: {
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
  },
  title: {
    fontSize: 16,
    color: colors.grey[900],
  },
  descriptionText: {
    fontSize: 14,
    color: colors.grey[500],
  },
  icon: {
    overflow: "hidden",
  },
  right: {
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: 16,
  },
  contentContainer: {
    ...backgroundColorStyles.bgLight,
    padding: 24,
  },
});

export default CourseSettingsAccordion;
